import { useEffect, useMemo, useRef } from 'react';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Text,
  useMediaQuery,
  Box,
  Button,
  HStack,
  Tooltip,
  useColorMode,
  Flex,
  Badge,
  Circle,
  Heading,
  useDisclosure,
  Stack,
} from '@chakra-ui/react';
import { useTable, usePagination } from 'react-table';
import TableBox from '../../common/TableBox';
import EmptyState from '../../../common/EmptyState';
import WebhookMoreInfo from './WebhookMoreInfo';
import WebhookSubscriptionModal from '../../modals/WebhookSubscriptionModal';
import { useIsReadOnly } from '../../../hooks/useIsReadOnly';
import LockedActionButton from '../../common/LockedActionButton';
import ReactTablePagination from '../../../common/ReactTablePagination';

export default function WebhooksTable({
  columns,
  data,
  isLoading,
  fetchData,
  heading = 'Webhook subscriptions',
  total = 0,
  pageCount = 0,
}) {
  const isReadOnly = useIsReadOnly();
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setHiddenColumns,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 5 },
      manualPagination: true,
      pageCount,
    },
    usePagination
  );

  const {
    isOpen: isWebhookSubscriptionOpen,
    onOpen: onWebhookSubscriptionOpen,
    onClose: onWebhookSubscriptionClose,
  } = useDisclosure();

  const [gtLG] = useMediaQuery('(min-width: 62em)');
  const [gtMD] = useMediaQuery('(min-width: 48em)');
  const [gtSM] = useMediaQuery('(min-width: 30em)');

  useEffect(() => {
    fetchData({ pageIndex, pageSize });
  }, [fetchData, pageIndex, pageSize]);

  useEffect(() => {
    if (gtLG) {
      setHiddenColumns([]);
    } else if (gtMD) {
      setHiddenColumns(['events']);
    } else if (gtSM) {
      setHiddenColumns(['events', 'scope']);
    } else {
      setHiddenColumns(['events', 'scope', 'enabled']);
    }
  }, [gtLG, gtMD, gtSM, setHiddenColumns]);

  if (isLoading) {
    return (
      <Spinner
        width="52px"
        height="52px"
        thickness="4px"
        speed="0.65s"
        emptyColor="quinaryBackground"
        mt={4}
        mx="auto"
      />
    );
  }

  return (
    <>
      <TableBox>
        <HStack justifyContent="space-between" mb="16px">
          <Heading size="lg" mb="16px">
            {heading} {total ? `(${total})` : ''}
          </Heading>

          {isReadOnly ? (
            <LockedActionButton
              alignSelf={{ base: 'flex-start', sm: 'center' }}
              mt={{ base: '12px', sm: '0' }}
              size="sm"
            >
              {gtSM ? 'Add subscription' : 'Add'}
            </LockedActionButton>
          ) : (
            <Button
              alignSelf={{ base: 'flex-start', sm: 'center' }}
              mt={{ base: '12px', sm: '0' }}
              alt="Create webhook"
              size="sm"
              onClick={onWebhookSubscriptionOpen}
            >
              {gtSM ? 'Add subscription' : 'Add'}
            </Button>
          )}
        </HStack>

        {data.length > 0 ? (
          <Table {...getTableProps()} size="sm">
            <Thead>
              {headerGroups.map((hg) => (
                <Tr {...hg.getHeaderGroupProps()} pointerEvents="none">
                  {hg.headers.map((col) => (
                    <Th {...col.getHeaderProps()}>{col.render('Header')}</Th>
                  ))}
                </Tr>
              ))}
            </Thead>

            <Tbody {...getTableBodyProps()}>
              {rows.map((row) => {
                prepareRow(row);
                return (
                  <Tr {...row.getRowProps()}>
                    {row.cells.map((cell) => (
                      <Td {...cell.getCellProps()}>
                        {cell.column.id === 'enabled' ? (
                          cell.value ? (
                            <Flex justifyContent="center">
                              <Circle size="15px" bg="success" />
                            </Flex>
                          ) : (
                            <Flex justifyContent="center">
                              <Circle size="15px" bg="removed" />
                            </Flex>
                          )
                        ) : cell.column.id === 'scope' ? (
                          <Text>
                            {cell.row.original.scopeType === 'entity'
                              ? cell.row.original.scopeName
                              : 'Org‑wide'}
                          </Text>
                        ) : cell.column.id === 'events' ? (
                          <Flex direction="row" gap={2} flexWrap="wrap">
                            {cell.value.map((event) => (
                              <Badge
                                variant="removed"
                                key={event}
                                px={3}
                                py={1.5}
                              >
                                {event}
                              </Badge>
                            ))}
                          </Flex>
                        ) : cell.column.id === 'actions' ? (
                          <WebhookMoreInfo cell={cell} fetchData={fetchData} />
                        ) : (
                          <Text wordBreak="break-word">
                            {cell.render('Cell')}
                          </Text>
                        )}
                      </Td>
                    ))}
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        ) : (
          <EmptyState
            heading="No webhook subscriptions"
            text="Add an endpoint to start receiving events."
          />
        )}
        {data.length > 0 && (
          <Flex
            mt={6}
            flexDirection={{ base: 'column-reverse', '2xl': 'row' }}
            justifyContent={{ base: 'none', '2xl': 'space-between' }}
          >
            <Stack
              spacing="18px"
              direction={{ base: 'column', sm: 'row' }}
              shouldWrapChildren="true"
            ></Stack>

            <ReactTablePagination
              canPreviousPage={canPreviousPage}
              canNextPage={canNextPage}
              pageOptions={pageOptions}
              pageCount={pageCount}
              gotoPage={gotoPage}
              nextPage={nextPage}
              previousPage={previousPage}
              setPageSize={setPageSize}
              pageIndex={pageIndex}
              pageSize={pageSize}
            />
          </Flex>
        )}
      </TableBox>
      <WebhookSubscriptionModal
        isOpen={isWebhookSubscriptionOpen}
        onClose={onWebhookSubscriptionClose}
        fetchData={fetchData}
      />
    </>
  );
}
